import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { CalendarDays, Plus } from 'lucide-react';
import { Link, useNavigate } from 'react-router';
import { WorkOrderStatusBadge } from '../../components/work-order-badges';
import { Button, Card, EmptyState, ErrorState, PageHeader, PageLoader, Pagination, Segmented, Table, Td, Th } from '../../components/ui';
import { api } from '../../lib/api';
import { workOrderHref } from '../../lib/desk';
import { eventPlaceLabel, TEAM_LABEL, type EventRequestPage } from '../../lib/events';
import { formatDateTime } from '../../lib/format';
import { useUrlState } from '../../lib/hooks';

const PAGE_SIZE = 25;

type When = 'upcoming' | 'past';
type Scope = 'all' | 'mine';

/** Event setup requests: the signed-in person's, or everyone's for staff on either help desk. */
export function EventRequestsPage() {
  const navigate = useNavigate();
  const [state, setState] = useUrlState({ when: 'upcoming', scope: 'all', page: '1' });
  const page = Number(state.page) || 1;
  const requests = useQuery({
    queryKey: ['event-requests', state],
    queryFn: () => api.get<EventRequestPage>('/event-requests', { when: state.when, scope: state.scope, page, pageSize: PAGE_SIZE }),
    placeholderData: keepPreviousData,
  });
  const seesAll = requests.data?.seesAll ?? false;

  return (
    <>
      <PageHeader
        title="Event requests"
        description="Ask for an event to be set up. The technology team and facilities each get a work order with the details."
        actions={
          <Button variant="primary" icon={<Plus />} onClick={() => navigate('/events/new')}>
            Request an event setup
          </Button>
        }
      />

      <Card>
        <div className="flex flex-wrap items-center gap-3 border-b border-slate-100 p-4">
          <Segmented<When>
            className="w-52"
            value={state.when === 'past' ? 'past' : 'upcoming'}
            onChange={(when) => setState({ when })}
            options={[
              { value: 'upcoming', label: 'Upcoming' },
              { value: 'past', label: 'Past' },
            ]}
          />
          {seesAll && (
            <Segmented<Scope>
              className="w-52"
              value={state.scope === 'mine' ? 'mine' : 'all'}
              onChange={(scope) => setState({ scope })}
              options={[
                { value: 'all', label: 'Everyone’s' },
                { value: 'mine', label: 'Mine' },
              ]}
            />
          )}
        </div>

        {requests.isLoading ? (
          <PageLoader />
        ) : requests.error ? (
          <ErrorState error={requests.error} onRetry={() => requests.refetch()} />
        ) : requests.data!.items.length === 0 ? (
          <EmptyState
            icon={<CalendarDays />}
            title={state.when === 'past' ? 'No past events' : 'No upcoming events'}
            description="Event setup requests appear here with the work orders they opened."
          />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>Event</Th>
                  <Th>When</Th>
                  <Th>Where</Th>
                  <Th>Work orders</Th>
                  {seesAll && <Th>Requested by</Th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {requests.data!.items.map((event) => (
                  <tr key={event.id} className="cursor-pointer hover:bg-slate-50/80" onClick={() => navigate(`/events/${event.id}`)}>
                    <Td className="max-w-[18rem]">
                      <Link to={`/events/${event.id}`} onClick={(e) => e.stopPropagation()} className="font-medium text-slate-900 hover:text-brand-700">
                        {event.title}
                      </Link>
                      <p className="truncate text-xs text-slate-500">
                        {event.eventType} · {event.attendees.toLocaleString()} attendees
                      </p>
                    </Td>
                    <Td className="whitespace-nowrap">{formatDateTime(event.startsAt)}</Td>
                    <Td>{eventPlaceLabel(event)}</Td>
                    <Td>
                      <div className="flex flex-col gap-1">
                        {event.workOrders.map((ticket) => (
                          <Link
                            key={ticket.id}
                            to={workOrderHref(ticket.service, ticket.id)}
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-2 text-xs whitespace-nowrap"
                          >
                            <span className="font-medium text-brand-800 hover:underline">#{ticket.number}</span>
                            <span className="text-slate-500">{TEAM_LABEL[ticket.service]}</span>
                            <WorkOrderStatusBadge status={ticket.status} />
                          </Link>
                        ))}
                      </div>
                    </Td>
                    {seesAll && <Td className="whitespace-nowrap">{event.requesterName}</Td>}
                  </tr>
                ))}
              </tbody>
            </Table>
            <Pagination page={page} pageSize={PAGE_SIZE} total={requests.data!.total} onPage={(next) => setState({ page: String(next) }, { resetPage: false })} />
          </>
        )}
      </Card>
    </>
  );
}
